import {
  FormSubmissionRequestFormType,
  FormSubmissionRequestState,
  FormType,
} from '@urbanx/agx.campaigns.client';
import { useContext, useRef } from 'react';
import { useAppSelector } from './useAppSelector';
import { useAppDispatch } from './useAppDispatch';
import { CampaignsApiContext } from '@urbanx/agx.campaigns.client';
import { markCampaignsAsStale } from '../features/campaigns/campaignsReducer';
import { FormConfigType } from '@urbanx/agx-ui-components';
import { saveFormMarketing } from 'Api/Marketing/marketingApi';
import { AuthContext } from '@urbanx/agx.auth.client';
import { useCampaignStaleMarker } from './useCampaignStaleMarker';
import { normalizeFormDates } from 'utils/normalizeFormDates';

export { normalizeFormDates } from 'utils/normalizeFormDates';

// Dates are already normalized before proposal clear semantics are applied.
function normalizeProposalClears(data: unknown): unknown {
  if (Array.isArray(data)) return data.map(normalizeProposalClears);
  if (data !== null && typeof data === 'object') {
    return Object.fromEntries(
      Object.entries(data).map(([key, value]) => [
        key,
        value === undefined ? null : normalizeProposalClears(value),
      ])
    );
  }
  return data;
}

export const useFormSaver = () => {
  const campaigns = useContext(CampaignsApiContext);
  const pendingSave = useRef(Promise.resolve());
  const { getAuthToken } = useContext(AuthContext);
  const dispatch = useAppDispatch();
  const markCampaignAsStale = useCampaignStaleMarker();
  const { campaignId, formId, selectedForm, formValues, pageStatuses } = useAppSelector(
    (state) => state.form
  );

  const persist = async (formConfigType?: FormConfigType) => {
    if (!campaignId || !formId || !selectedForm) throw new Error('Form identity is missing.');
    const { formType, state } = selectedForm;
    const formData = normalizeFormDates(formValues);

    switch (formConfigType) {
      case FormConfigType.Campaign: {
        const result = await campaigns
          .saveForm({
            campaignId,
            formId,
            formType: FormSubmissionRequestFormType[formType],
            state: FormSubmissionRequestState[state],
            formData:
              formType === FormType.ListingProposal ? normalizeProposalClears(formData) : formData,
            pageStatuses,
          })
          .catch((failure: unknown) => {
            if (formType === FormType.ListingProposal && typeof failure === 'string') {
              throw new Error(failure);
            }
            throw failure;
          });
        if (!result.savedSuccessfully) throw new Error('Failed to save form campaign.');
        dispatch(markCampaignsAsStale());
        void markCampaignAsStale(campaignId).catch(console.error);
        return;
      }
      case FormConfigType.Marketing: {
        const token = await getAuthToken();
        if (!token) throw new Error('Failed to authenticate form save.');
        const saved = await saveFormMarketing(
          token,
          campaignId,
          formId,
          formType,
          state,
          formData,
          pageStatuses
        );
        if (!saved) throw new Error('Failed to save form marketing package.');
        return;
      }
      default:
        throw new Error('Form configuration type is missing.');
    }
  };

  return (formConfigType?: FormConfigType) => {
    const operation = pendingSave.current.then(() => persist(formConfigType));
    // Keep the queue usable after a failure; the returned operation still rejects to its caller.
    pendingSave.current = operation.catch(() => {});
    return operation;
  };
};
